/**
 * Cross-filtering globale della dashboard (PowerBI-style).
 *
 * Tutti i widget leggono i filtri attivi da questo Context e si ricalcolano.
 * Filtri vuoti (`[]` / `null`) = "no filter" → mostra tutto il periodo.
 *
 * I filtri sono client-side: i dati sono già scaricati una volta sola da
 * `useDashboardData`. Per i numeri tipici del cartongessista (<1000 preventivi
 * per org) la performance è trascurabile, e zero round-trip DB.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface DashboardFilters {
  /** Multi-select clienti. Vuoto = tutti i clienti. */
  customerIds: string[];
  /**
   * Multi-select status (gruppi F30). Vuoto = tutti.
   * Valori validi: 'draft' | 'sent' | 'won' | 'lost'.
   * I legacy (pending → sent, approved/contracted → won) sono mappati nel filtro.
   */
  statuses: string[];
  /** Singola stratigrafia per nome. null = tutte. */
  stratigraphyName: string | null;
  /** Singolo fornitore. null = tutti. */
  supplier: string | null;
}

const DEFAULT_FILTERS: DashboardFilters = {
  customerIds: [],
  statuses: [],
  stratigraphyName: null,
  supplier: null,
};

/**
 * Preset filtri salvato dall'utente.
 * Es. "Pipeline alta", "Vinti Saint-Gobain", "Solo cliente Rossi".
 */
export interface DashboardFilterPreset {
  id: string;
  name: string;
  filters: DashboardFilters;
  createdAt: string;
}

interface DashboardFiltersContextValue {
  filters: DashboardFilters;
  setFilters: React.Dispatch<React.SetStateAction<DashboardFilters>>;
  /** True se almeno un filtro è attivo (non default). */
  hasActiveFilters: boolean;
  /** Reset filtri allo stato vuoto. */
  resetFilters: () => void;
  /** Toggle di un singolo customerId (se presente, lo rimuove). */
  toggleCustomer: (id: string) => void;
  /** Toggle status. */
  toggleStatus: (s: string) => void;
  /** Set stratigrafia singola (null per disabilitare). Toggle se uguale alla corrente. */
  setStratigraphy: (name: string | null) => void;
  /** Set fornitore singolo. Toggle se uguale al corrente. */
  setSupplier: (s: string | null) => void;
  /** Preset salvati in localStorage. */
  presets: DashboardFilterPreset[];
  /** Salva i filtri correnti come nuovo preset. */
  savePreset: (name: string) => void;
  /** Applica un preset. */
  applyPreset: (id: string) => void;
  /** Elimina un preset. */
  deletePreset: (id: string) => void;
}

const DashboardFiltersContext = createContext<DashboardFiltersContextValue | null>(null);

const PRESETS_STORAGE_KEY = 'dashboard-filter-presets';

function readPresets(): DashboardFilterPreset[] {
  try {
    const raw = localStorage.getItem(PRESETS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePresets(presets: DashboardFilterPreset[]): void {
  try {
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(presets));
  } catch {
    // ignore
  }
}

export const DashboardFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filters, setFilters] = useState<DashboardFilters>(DEFAULT_FILTERS);
  const [presets, setPresets] = useState<DashboardFilterPreset[]>(() => readPresets());

  const hasActiveFilters = useMemo(() => (
    filters.customerIds.length > 0 ||
    filters.statuses.length > 0 ||
    !!filters.stratigraphyName ||
    !!filters.supplier
  ), [filters]);

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const toggleCustomer = useCallback((id: string) => {
    setFilters(prev => ({
      ...prev,
      customerIds: prev.customerIds.includes(id)
        ? prev.customerIds.filter(x => x !== id)
        : [...prev.customerIds, id],
    }));
  }, []);

  const toggleStatus = useCallback((s: string) => {
    setFilters(prev => ({
      ...prev,
      statuses: prev.statuses.includes(s)
        ? prev.statuses.filter(x => x !== s)
        : [...prev.statuses, s],
    }));
  }, []);

  const setStratigraphy = useCallback((name: string | null) => {
    setFilters(prev => ({
      ...prev,
      stratigraphyName: prev.stratigraphyName === name ? null : name,
    }));
  }, []);

  const setSupplier = useCallback((s: string | null) => {
    setFilters(prev => ({
      ...prev,
      supplier: prev.supplier === s ? null : s,
    }));
  }, []);

  const savePreset = useCallback((name: string) => {
    const cleanName = name.trim();
    if (!cleanName) return;
    const newPreset: DashboardFilterPreset = {
      id: typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `preset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: cleanName,
      filters: { ...filters, customerIds: [...filters.customerIds], statuses: [...filters.statuses] },
      createdAt: new Date().toISOString(),
    };
    setPresets(prev => {
      const next = [newPreset, ...prev].slice(0, 20);
      writePresets(next);
      return next;
    });
  }, [filters]);

  const applyPreset = useCallback((id: string) => {
    const preset = presets.find(p => p.id === id);
    if (!preset) return;
    setFilters({
      ...preset.filters,
      customerIds: [...preset.filters.customerIds],
      statuses: [...preset.filters.statuses],
    });
  }, [presets]);

  const deletePreset = useCallback((id: string) => {
    setPresets(prev => {
      const next = prev.filter(p => p.id !== id);
      writePresets(next);
      return next;
    });
  }, []);

  const value: DashboardFiltersContextValue = {
    filters,
    setFilters,
    hasActiveFilters,
    resetFilters,
    toggleCustomer,
    toggleStatus,
    setStratigraphy,
    setSupplier,
    presets,
    savePreset,
    applyPreset,
    deletePreset,
  };

  return (
    <DashboardFiltersContext.Provider value={value}>
      {children}
    </DashboardFiltersContext.Provider>
  );
};

export const useDashboardFilters = (): DashboardFiltersContextValue => {
  const ctx = useContext(DashboardFiltersContext);
  if (!ctx) {
    // Fallback no-op se usato fuori dal Provider: utile per testing / componenti riusabili.
    return {
      filters: DEFAULT_FILTERS,
      setFilters: () => undefined,
      hasActiveFilters: false,
      resetFilters: () => undefined,
      toggleCustomer: () => undefined,
      toggleStatus: () => undefined,
      setStratigraphy: () => undefined,
      setSupplier: () => undefined,
      presets: [],
      savePreset: () => undefined,
      applyPreset: () => undefined,
      deletePreset: () => undefined,
    };
  }
  return ctx;
};

/**
 * Helper: dato un estimate row, verifica se passa i filtri attivi.
 * - Customer: customerIds vuoto = passa; altrimenti deve combaciare con projects.customer_id
 * - Status: statuses vuoto = passa; mappa legacy pending→sent, contracted/approved→won
 * - Stratigraphy: null = passa; altrimenti almeno una sua estimate_stratigraphy deve avere quel nome
 * - Supplier: null = passa; almeno una stratigrafia con materiale di quel supplier
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function estimateMatchesFilters(estimate: any, filters: DashboardFilters): boolean {
  // Customer
  if (filters.customerIds.length > 0) {
    const custId = estimate?.projects?.customer_id;
    if (!custId || !filters.customerIds.includes(custId)) return false;
  }

  // Status (con normalizzazione legacy)
  if (filters.statuses.length > 0) {
    const s = estimate?.status as string;
    const normalized =
      s === 'pending' ? 'sent' :
      s === 'approved' || s === 'contracted' ? 'won' :
      s;
    if (!filters.statuses.includes(normalized)) return false;
  }

  // Stratigraphy
  if (filters.stratigraphyName) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stratis = (estimate?.estimate_stratigraphies as any[] | null) ?? [];
    const has = stratis.some(x => (x.name || 'Senza nome') === filters.stratigraphyName);
    if (!has) return false;
  }

  // Supplier
  if (filters.supplier) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stratis = (estimate?.estimate_stratigraphies as any[] | null) ?? [];
    let found = false;
    outer: for (const s of stratis) {
      const layers = (s?.stratigraphy_data?.layers as unknown[] | undefined) ?? [];
      for (const l of layers) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const supp = (l as any)?.materials?.supplier;
        if (supp === filters.supplier) { found = true; break outer; }
      }
    }
    if (!found) return false;
  }

  return true;
}
